import { Suspense, useState } from "react";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { PageLoader } from "@/components/ui/PageLoader";
import { pageSkeletonVariantFor } from "@/components/ui/pageSkeletons";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/hooks/useNotifications";
import { ProfileSheet } from "@/components/shell/ProfileSheet";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useScrollResetOnRouteChange } from "@/hooks/useScrollResetOnRouteChange";
import { useTranslation } from "react-i18next";
import { AssistantProvider } from "@/contexts/AssistantContext";
import { AssistantButton } from "@/components/assistant/AssistantButton";
import { OnboardingProvider } from "@/contexts/OnboardingContext";
import { OnboardingProgressBar } from "@/components/onboarding/OnboardingProgressBar";
import { useCurrentPageTitle } from "@/contexts/PageTitleContext";
import { Menu, ArrowLeft } from "lucide-react";
import { PdfViewerProvider } from "@/contexts/PdfViewerContext";
import { PdfViewerOverlay } from "@/components/ui/PdfViewerOverlay";
import { MobileTopBar, HEADER_GRADIENT } from "@/components/shell/MobileTopBar";
import { MobileDock } from "@/components/shell/MobileDock";
import { OnboardingReturnBar } from "@/components/onboarding/OnboardingReturnBar";
import { PageTransition } from "@/components/shell/PageTransition";
import { OnboardingGate, useOnboardingGate } from "@/components/routing/OnboardingGate";
import { OnboardingHelpButton } from "@/components/onboarding/OnboardingHelpButton";
import { useOnboardingOptional } from "@/contexts/OnboardingContext";

import { cn } from "@/lib/utils";

function StickyPageHeader() {
  const { title, actions, leftActions, hideDesktopTopBar, onMobileBack, desktopBackOnly } = useCurrentPageTitle();
  const { toggleSidebar } = useSidebar();
  const { active: onboardingActive } = useOnboardingGate();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const [profileOpen, setProfileOpen] = useState(false);
  // Antes del early return: los hooks se llaman siempre en el mismo orden.
  const onboarding = useOnboardingOptional();

  if (!title || hideDesktopTopBar) return null;

  const fullName = user?.user_metadata?.full_name || user?.email || "U";
  const initial = fullName.charAt(0).toUpperCase();
  const hasUnread = (unreadCount ?? 0) > 0;


  const showOnboardingBack =
    onboardingActive && location.pathname !== "/getting-started";

  /* Durante la puesta en marcha la cabecera dice DÓNDE estás y CUÁNTO llevas:
     "Estructura · Paso 1 de 6". La página conserva su nombre — decir "Primeros
     Pasos" en las seis pantallas no orienta a nadie — y el contador va detrás.
     En la propia guía no se añade nada: su título ya es el del conjunto. */
  const stepIndex = onboardingActive && onboarding && location.pathname !== "/getting-started"
    ? onboarding.steps.findIndex((s) => {
        const base = s.route.split("?")[0];
        return location.pathname === base || location.pathname.startsWith(base + "/");
      })
    : -1;
  const displayTitle = stepIndex >= 0
    ? `${title} · ${t("guide.stepCounter", {
        defaultValue: "Paso {{current}} de {{total}}",
        current: stepIndex + 1,
        total: onboarding!.steps.length,
      })}`
    : title;

  return (
    <>
    <header
      data-app-topbar
      className="hidden md:flex shrink-0 z-40 relative items-center px-[18px] gap-3 rounded-[22px] shadow-[0_12px_30px_rgba(20,60,100,0.18)] ml-4 mr-3.5 min-h-[62px] overflow-hidden"
      style={{
        background: "var(--band)",
        /* LA BARRA DE ESTADO, TAMBIÉN AQUÍ (petición del usuario 31-08-2026).
           Esta cabecera es la de tableta y escritorio, y llevaba un `mt-3` a
           secas: en un iPad —donde el envoltorio nativo pinta la app por debajo
           del reloj, la cobertura y la batería— los 12 px no llegaban y el
           sistema se comía la parte de arriba de la banda azul. La de teléfono
           ya lo reservaba desde siempre (`--topbar-top-gap`); esta se quedó
           fuera de aquel arreglo.

           Se lee `--safe-top` (la variable del envoltorio nativo con
           `env(safe-area-inset-top)` de respaldo) y NO se le pone suelo: fuera
           de una app instalada vale cero y la cabecera se queda exactamente
           donde estaba, que es lo que se quiere en un navegador de escritorio.
           Los 0.75rem son el `mt-3` de antes, que por eso ya no está en las
           clases. */
        marginTop: "calc(0.75rem + var(--safe-top))",
      }}
    >
      {/* Decorative soft blobs — same vibe as mobile tall header */}
      <div aria-hidden className="pointer-events-none absolute -top-16 -right-12 w-48 h-48 rounded-full bg-white/12" />
      <div aria-hidden className="pointer-events-none absolute -bottom-20 right-32 w-40 h-40 rounded-full bg-white/8" />

      {/* Left zone: hamburger (desktop) / profile (tablet) + leftActions + title */}
      <div className="flex items-center gap-2 min-w-0 relative z-10">
        {/* Desktop (lg+): hamburger toggles the sidebar collapse (hidden on subpages — back arrow only) */}
        {!desktopBackOnly && !onMobileBack && (
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex w-[38px] h-[38px] rounded-[11px] bg-white/[0.18] border border-white/30 items-center justify-center hover:bg-white/25 transition-colors flex-shrink-0"
            aria-label="Toggle menu"
          >
            <Menu className="w-5 h-5 text-white" />
          </button>
        )}
        {/* Tablet (md–lg): profile avatar — hidden on subpages (back arrow only) */}
        {!desktopBackOnly && !onMobileBack && (
          <button
            onClick={() => setProfileOpen(true)}
            className="flex lg:hidden relative w-[38px] h-[38px] rounded-full bg-white/25 border border-white/30 items-center justify-center text-white font-bold text-sm hover:bg-white/30 transition-colors flex-shrink-0"
            aria-label={t("nav.profile", "Profile")}
          >
            {initial}
            {hasUnread && (
              <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-rose-500 ring-2 ring-white/80" />
            )}
          </button>
        )}
        {/* Sub-page back — arrow + "Atrás" label on tablet/desktop (mobile shows arrow only) */}
        {onMobileBack && (
          <button
            onClick={onMobileBack}
            className={cn(
              "inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-sm font-semibold hover:bg-white/30 active:scale-95 transition-all flex-shrink-0",
              /* En escritorio manda «Volver a la guía»: dos flechas de volver
                 seguidas obligan a leer para saber cuál es cuál. Por debajo de `lg`
                 este se queda, porque allí el que desaparece es el otro. */
              showOnboardingBack && "lg:hidden",
            )}
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t("common.back")}</span>
          </button>
        )}
        {showOnboardingBack && (
          <button
            onClick={() => navigate("/getting-started")}
            /* Solo en escritorio. Por debajo de `lg` la vuelta a la guía es la barra
               naranja de abajo (`OnboardingReturnBar`), y tenerla también aquí
               dejaba dos botones que llevan al mismo sitio en la misma pantalla. */
            className="hidden lg:inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-sm font-semibold hover:bg-white/30 active:scale-95 transition-all flex-shrink-0"
            aria-label={t("guide.returnBar.label")}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t("guide.returnBar.label", "Back to setup")}</span>
          </button>
        )}
        {/* leftActions render on every viewport, sitting next to the hamburger on mobile and inline on desktop */}
        {leftActions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {leftActions}
          </div>
        )}
        {/* Title visible only on mobile/tablet, left-aligned */}
        <h1 className="md:hidden text-base font-bold text-white truncate">
          {displayTitle}
        </h1>
      </div>

      {/* Center zone: title centered on desktop */}
      <h1 className="hidden md:flex absolute inset-0 items-center justify-center font-['Poppins'] font-bold text-xl tracking-[-0.01em] text-white pointer-events-none z-10">
        {displayTitle}
      </h1>

      {/* Right zone: actions. El socorro por WhatsApp se pinta SIEMPRE mientras
          dure la puesta en marcha, tenga la página acciones propias o no. */}
      {(actions || onboardingActive) && (
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto md:mr-6 relative z-10">
          {actions}
          {onboardingActive && <OnboardingHelpButton />}
        </div>
      )}
    </header>
    <ProfileSheet open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}

export function MainLayout() {
  const location = useLocation();
  const isAssistantRoute = location.pathname.startsWith("/advanced");
  /* El asistente geométrico se salta la transición de página por el MISMO motivo
     que la ruta del asistente, aunque el síntoma sea otro: `PageTransition` envuelve
     la ruta en un `motion.div` y framer-motion le deja un `transform`. Un ancestro
     transformado pasa a ser el bloque contenedor de sus descendientes `position:
     fixed`, así que el mapa a pantalla completa (`fixed inset-0`) dejaba de medir la
     pantalla y medía el hueco de contenido — con la cabecera asomando por arriba y
     sin llegar nunca a las zonas seguras del móvil. */
  const isDesignerRoute = location.pathname.startsWith("/designer");
  const skipPageTransition = isAssistantRoute || isDesignerRoute;

  useScrollResetOnRouteChange(() => [
    document.getElementById("main-scroll-container"),
  ]);


  return (
    <AssistantProvider>
      <OnboardingProvider>
          <PdfViewerProvider>
            <SidebarProvider className="ulture-sidebar-provider" style={{ "--sidebar-width-icon": "5rem" } as any}>
              <div
                className="h-[100dvh] flex w-full max-w-[100vw] overflow-hidden"
                style={{ background: "var(--bg-page)" }}
              >
                <AppSidebar />
                
                <main className="flex-1 min-h-0 flex flex-col overflow-hidden">
                  <MobileTopBar />
                  <StickyPageHeader />
                  
                  <OnboardingProgressBar />
                  
                <div
                  id="main-scroll-container"
                  className={cn(
                    "relative flex-1 min-h-0 max-w-full flex flex-col",
                    isAssistantRoute
                      ? "overflow-hidden pb-0"
                      : "dock-safe-scroll overflow-y-auto overflow-x-auto"
                  )}
                >
                  {skipPageTransition ? (
                    /* `w-full` a secas es exactamente lo que pinta `PageTransition`
                       sin animar, así que el diseñador conserva su maquetación.
                       En el ORDENADOR el asistente geométrico pide además saber
                       cuánto mide la pantalla: sus pasos llevan un dibujo que
                       tiene que quedarse con el hueco que sobra, y para eso el
                       hueco tiene que existir como número. De `lg` para abajo se
                       queda como estaba —altura natural— porque en el teléfono
                       el paso se recorre bajando, que es lo correcto. */
                    <div
                      className={
                        isAssistantRoute
                          ? "flex-1 min-h-0 flex flex-col w-full"
                          : isDesignerRoute
                            ? "w-full lg:flex-1 lg:min-h-0 lg:flex lg:flex-col"
                            : "w-full"
                      }
                    >
                      <Suspense fallback={<PageLoader variant={pageSkeletonVariantFor(location.pathname)} />}>
                        <OnboardingGate>
                          <Outlet />
                        </OnboardingGate>
                      </Suspense>
                    </div>
                  ) : (
                    <PageTransition>
                      <Suspense fallback={<PageLoader variant={pageSkeletonVariantFor(location.pathname)} />}>
                        <OnboardingGate>
                          <Outlet />
                        </OnboardingGate>
                      </Suspense>
                    </PageTransition>
                  )}
                </div>
                <MobileDock />
                {/* Ocupa el hueco que deja MobileDock durante la puesta en marcha
                    (se oculta a propósito para que nadie se salte los pasos). Sin
                    esto, por debajo de `lg` no había forma de volver a la guía. */}
                <OnboardingReturnBar />
                </main>
              </div>
              
              {/* AssistantButton renders the assistant/voice panels for both desktop and mobile.
                  Its floating bottom-right trigger is hidden on mobile via `hidden lg:flex`;
                  on mobile the dock's center mic opens the panel via togglePanel(). */}
              <AssistantButton />
              
              <PdfViewerOverlay />
            </SidebarProvider>

          </PdfViewerProvider>
      </OnboardingProvider>
    </AssistantProvider>
  );
}
